import { createContext, use, useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  BanknoteIcon,
  EyeIcon,
  FlagIcon,
  HourglassIcon,
  MilestoneIcon,
  MousePointerClickIcon,
  TargetIcon,
  TrendingUpIcon,
  ZapIcon,
  type LucideIcon,
} from "lucide-react";
import {
  formatCompact,
  formatMoney,
  formatMoneyCompact,
  formatRate,
  formatRoas,
  type CreativeExtras,
  type RankMetric,
  type VideoMetrics,
} from "@scandi/shared";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { roasClass } from "@/components/leaderboard/metric-chip";
import { cn } from "@/lib/utils";

/**
 * The one place a metric's name, meaning, icon, and formatting are defined.
 *
 * The leaderboard, editor totals, video cards, and ad rows all read from here,
 * so a label can never drift from the number underneath it and the same metric
 * looks the same everywhere it appears.
 */
/**
 * Anything a metric can be read off. Retention rates are optional because
 * editor totals deliberately omit them, and the click and completion rates
 * because only the single-creative view has room for them. Delivery and money
 * always exist.
 */
export type MetricSource = Omit<VideoMetrics, "hookRate" | "holdRate"> &
  Partial<Pick<VideoMetrics, "hookRate" | "holdRate">> &
  Partial<CreativeExtras>;

export type MetricKey =
  | "impressions"
  | "spend"
  | "revenue"
  | "roas"
  | "hookRate"
  | "holdRate"
  | "ctr"
  | "p75Rate"
  | "p95Rate";

export interface MetricSpec {
  key: MetricKey;
  label: string;
  /** Abbreviation for narrow table headers. */
  short: string;
  hint: string;
  icon: LucideIcon;
  format: (m: MetricSource, currency: string) => string;
  /**
   * The same number at headline size, shortened to fit: `1.0M`, `€16.3k`.
   *
   * Only the two volume metrics have one, because only they can lead a card —
   * a rate is already short and a ROAS has nothing to compact.
   */
  compact?: (m: MetricSource, currency: string) => string;
  /** Only ROAS carries a colour; the rest have no agreed good/bad line. */
  colorOf?: (m: MetricSource, greenThreshold: number) => string;
  /**
   * Fixed column width. Declared once here so the header, the hook variants,
   * and the ads nested under them stay in the same columns at every depth.
   */
  width: string;
  /** Dropped on phones, where five columns will not fit beside a label. */
  minorOnMobile?: boolean;
}

/**
 * Everything about a metric except its words. The names, abbreviations and
 * tooltips live in the translation catalog under `metrics.<key>`, and are joined
 * onto these by `useMetrics` — a metric's identity is its formula and its icon,
 * which do not change with the reader's language.
 */
type MetricShape = Omit<MetricSpec, "label" | "short" | "hint">;

const SHAPES: Record<MetricKey, MetricShape> = {
  impressions: {
    key: "impressions",
    icon: EyeIcon,
    width: "w-14",
    format: (m) => formatCompact(m.impressions),
    compact: (m) => formatCompact(m.impressions),
  },
  spend: {
    key: "spend",
    icon: BanknoteIcon,
    width: "w-16",
    format: (m, currency) => formatMoney(m.spend, currency),
    compact: (m, currency) => formatMoneyCompact(m.spend, currency),
    minorOnMobile: true,
  },
  revenue: {
    key: "revenue",
    icon: TrendingUpIcon,
    width: "w-16",
    format: (m, currency) => formatMoney(m.revenue, currency),
  },
  roas: {
    key: "roas",
    icon: TargetIcon,
    width: "w-14",
    format: (m) => formatRoas(m.roas),
    colorOf: (m, greenThreshold) => roasClass(m.roas, greenThreshold),
  },
  hookRate: {
    key: "hookRate",
    icon: ZapIcon,
    width: "w-12",
    format: (m) => formatRate(m.hookRate),
  },
  holdRate: {
    key: "holdRate",
    icon: HourglassIcon,
    width: "w-12",
    format: (m) => formatRate(m.holdRate),
  },
  ctr: {
    key: "ctr",
    icon: MousePointerClickIcon,
    width: "w-12",
    format: (m) => formatRate(m.ctr, 2),
  },
  p75Rate: {
    key: "p75Rate",
    icon: MilestoneIcon,
    width: "w-12",
    format: (m) => formatRate(m.p75Rate),
  },
  p95Rate: {
    key: "p95Rate",
    icon: FlagIcon,
    width: "w-12",
    format: (m) => formatRate(m.p95Rate),
  },
};

/** The groupings each screen shows, in the order they are read. */
const GROUPS = {
  /** Group and video rows: delivery, money, then retention. */
  row: ["impressions", "spend", "roas", "hookRate", "holdRate"],
  /**
   * Editor totals. Retention rates are absent on purpose: averaged across every
   * concept an editor shipped, a hook rate is a number with nothing to compare
   * it to. It earns its place on a video, next to its sibling hooks.
   */
  totals: ["impressions", "spend", "revenue", "roas"],
  /**
   * What a creative delivered and what it cost — the four numbers a payout is
   * argued from, given a row of their own so the money reads at a glance.
   */
  creative: ["impressions", "spend", "revenue", "roas"],
  /**
   * How the audience reacted. Rates rather than totals, and a row below the
   * money rather than beside it: seven cards abreast squeezed "hook rate" onto
   * two lines and left every number cramped.
   *
   * The completion milestones are deliberately absent — they are only readable
   * as a curve, so they live with the drop-off chart instead of being scattered
   * here as five percentages nobody can hold in their head at once.
   */
  engagement: ["hookRate", "holdRate", "ctr"],
} satisfies Record<string, MetricKey[]>;

/**
 * Which metric the app is currently ranked on.
 *
 * Context rather than a prop because it decides the shape of nearly every
 * number on screen — which one leads a card, what order the tiles sit in, how
 * a share is worded — and threading it beside `currency` through six levels of
 * cards and rows would put it in the signature of components that do nothing
 * with it but pass it on.
 *
 * Each page sets it from its own response. Impressions is the default, so a
 * screen that never sets it still renders the way it always did.
 */
const RankMetricContext = createContext<RankMetric>("impressions");

export function RankMetricProvider({
  value,
  children,
}: {
  /** Undefined while the page's query is still loading. */
  value: RankMetric | undefined;
  children: ReactNode;
}) {
  return (
    <RankMetricContext value={value ?? "impressions"}>{children}</RankMetricContext>
  );
}

export function useRankMetric(): RankMetric {
  return use(RankMetricContext);
}

/** The two additive metrics a board can be ranked by. */
const VOLUME = new Set<MetricKey>(["impressions", "spend"]);

export type HeadlineSpec = MetricSpec & Required<Pick<MetricSpec, "compact">>;

/** The ranking metric leads every group it belongs to; the rest keep their order. */
function ordered(keys: MetricKey[], rank: RankMetric): MetricKey[] {
  return keys.includes(rank) ? [rank, ...keys.filter((k) => k !== rank)] : keys;
}

/**
 * The metrics, named in the reader's language and ordered by what the board is
 * ranked on.
 *
 * A hook rather than a constant because the names are translations, and a
 * constant would be built once in whatever language happened to be active at
 * import time and then never change again.
 */
export function useMetrics() {
  const { t } = useTranslation();
  const rank = useRankMetric();

  return useMemo(() => {
    const named = Object.fromEntries(
      (Object.keys(SHAPES) as MetricKey[]).map((key) => [
        key,
        {
          ...SHAPES[key],
          label: t(`metrics.${key}.label`),
          short: t(`metrics.${key}.short`),
          // The ranking metric says so in its own tooltip: it is the one number
          // on the row whose size decided the order of the list.
          hint:
            key === rank
              ? `${t(`metrics.${key}.hint`)} ${t("metrics.ranksOnThis")}`
              : t(`metrics.${key}.hint`),
          // Impressions and spend are the two widest columns and a phone has
          // room for one of them, so the one that is not carrying the ranking
          // steps aside. Which one that is has to follow the setting, or spend
          // mode would drop the very number the row is ordered by.
          ...(VOLUME.has(key) && { minorOnMobile: key !== rank }),
        },
      ]),
    ) as Record<MetricKey, MetricSpec>;

    const group = (keys: MetricKey[]) => ordered(keys, rank).map((key) => named[key]);

    return {
      metrics: named,
      /**
       * The metric the ranking is built on, promoted to headline everywhere.
       * Narrowed to the volume metrics, which are the only ones that can rank a
       * board and so the only ones that always have a compact form.
       */
      headline: named[rank] as HeadlineSpec,
      rank,
      row: group(GROUPS.row),
      totals: group(GROUPS.totals),
      creative: group(GROUPS.creative),
      engagement: group(GROUPS.engagement),
    };
  }, [t, rank]);
}

/** Right-aligned metric cells, in the shared column widths. */
export function MetricCells({
  metrics,
  currency,
  roasGreenThreshold,
  muted = false,
}: {
  metrics: MetricSource;
  currency: string;
  roasGreenThreshold: number;
  /** Ads sit under a variant that already carries the emphasis. */
  muted?: boolean;
}) {
  const { row, rank } = useMetrics();

  return (
    <>
      {row.map((metric) => (
        <span
          key={metric.key}
          className={cn(
            "shrink-0 text-right text-xs tabular-nums",
            metric.width,
            metric.minorOnMobile && "hidden sm:inline",
            muted || !(metric.key === rank || metric.key === "roas")
              ? "text-muted-foreground"
              : "font-medium",
            metric.colorOf?.(metrics, roasGreenThreshold),
          )}
        >
          {metric.format(metrics, currency)}
        </span>
      ))}
    </>
  );
}

/** Column labels, stated once above the rows they describe. */
export function MetricHeaderCells() {
  const { row } = useMetrics();

  return (
    <>
      {row.map((metric) => (
        <span
          key={metric.key}
          className={cn(
            "text-muted-foreground shrink-0 text-right text-[10px] font-medium tracking-wider uppercase",
            metric.width,
            metric.minorOnMobile && "hidden sm:inline",
          )}
        >
          <Tooltip>
            <TooltipTrigger className="cursor-help">
              {metric.short}
            </TooltipTrigger>
            <TooltipContent className="max-w-56">{metric.hint}</TooltipContent>
          </Tooltip>
        </span>
      ))}
    </>
  );
}
